import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDateTime, formatDuration, getStatusColor } from '../utils/crewDutyRosterUtils';

const getRestStatusColor = (status) => {
  switch (status) {
    case 'ON_DUTY':
      return 'warning';
    case 'RESTING':
      return 'info';
    case 'REST_COMPLETE':
      return 'success';
    case 'NO_PRIOR_DUTY':
      return 'default';
    default:
      return 'default';
  }
};

const getRestStatusLabel = (status) => {
  switch (status) {
    case 'ON_DUTY':
      return 'On duty';
    case 'RESTING':
      return 'Resting';
    case 'REST_COMPLETE':
      return 'Rest complete';
    case 'NO_PRIOR_DUTY':
      return 'No prior duty';
    default:
      return status || '—';
  }
};

/**
 * Dashboard View component for Crew Duty Roster
 */
const CrewDutyRosterDashboard = ({ dashboardData }) => {
  const pilotHoursByAircraft = dashboardData?.pilotHoursByAircraft || [];
  const crewRestHours = dashboardData?.crewRestHours || [];
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey]
    }));
  };

  const formatDutyLabel = (trip) => {
    if (trip.dutyType === 'TRAINING') return 'Flight Training';
    return 'Flight';
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>Total Crew</Typography>
            <Typography variant="h4">{dashboardData?.totalCrew || 0}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>Active Duties Today</Typography>
            <Typography variant="h4">{dashboardData?.activeDutiesToday || 0}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>Pending Leave Requests</Typography>
            <Typography variant="h4">{dashboardData?.pendingLeaveRequests || 0}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>Crew Approaching Limits</Typography>
            <Typography variant="h4">{dashboardData?.crewApproachingLimits || 0}</Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Crew rest hours
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Tracks required rest after each crew member&apos;s last duty (DGCA rest period),
              how much rest has accrued, and how much remains. Roster-plan / scheduled-off
              entries are excluded.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                size="small"
                color="info"
                label={`Resting: ${dashboardData?.crewRestingCount ?? crewRestHours.filter((r) => r.status === 'RESTING').length}`}
              />
              <Chip
                size="small"
                color="success"
                label={`Rest complete: ${dashboardData?.crewRestCompleteCount ?? crewRestHours.filter((r) => r.status === 'REST_COMPLETE').length}`}
              />
              <Chip
                size="small"
                color="warning"
                label={`On duty: ${crewRestHours.filter((r) => r.status === 'ON_DUTY').length}`}
              />
            </Box>
            {crewRestHours.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No crew rest data available yet.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Crew</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last duty end</TableCell>
                      <TableCell>Last duty / charter</TableCell>
                      <TableCell align="right">Rest required</TableCell>
                      <TableCell align="right">Rest accrued</TableCell>
                      <TableCell align="right">Rest remaining</TableCell>
                      <TableCell>Rest ends</TableCell>
                      <TableCell>Next duty start</TableCell>
                      <TableCell align="right">Gap to next</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {crewRestHours.map((row) => (
                      <TableRow key={String(row.crewId)} hover>
                        <TableCell>{row.crewName || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={getRestStatusLabel(row.status)}
                            size="small"
                            color={getRestStatusColor(row.status)}
                          />
                          {row.status === 'ON_DUTY' && row.hoursOnDuty != null && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              On duty {formatDuration(row.hoursOnDuty)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(row.lastDutyEnd)}</TableCell>
                        <TableCell>
                          {row.status === 'ON_DUTY' ? (
                            <>
                              {row.currentDutyType || '—'}
                              {row.currentCharterNumber && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {row.currentCharterNumber}
                                </Typography>
                              )}
                              {row.currentDutyStart && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Started {formatDateTime(row.currentDutyStart)}
                                </Typography>
                              )}
                            </>
                          ) : (
                            <>
                              {row.lastDutyType || '—'}
                              {row.lastCharterNumber && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {row.lastCharterNumber}
                                </Typography>
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {row.restRequiredHours != null
                            ? formatDuration(row.restRequiredHours)
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {row.restAccruedHours != null
                            ? formatDuration(row.restAccruedHours)
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {row.restRemainingHours != null
                            ? formatDuration(row.restRemainingHours)
                            : '—'}
                        </TableCell>
                        <TableCell>{formatDateTime(row.restPeriodEnd)}</TableCell>
                        <TableCell>{formatDateTime(row.nextDutyStart)}</TableCell>
                        <TableCell align="right">
                          {row.hoursUntilNextDuty != null
                            ? formatDuration(row.hoursUntilNextDuty)
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pilot flying hours by aircraft
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Total flight time (from FLIGHT duties and Flight Training) per pilot per aircraft.
              Expand a row to see charter/trip details and times.
            </Typography>
            {pilotHoursByAircraft.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No flight hours recorded yet.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={48} />
                      <TableCell>Pilot</TableCell>
                      <TableCell>Aircraft</TableCell>
                      <TableCell>Aircraft Type</TableCell>
                      <TableCell align="right">Total flight hours</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pilotHoursByAircraft.map((row, idx) => {
                      const rowKey = `${row.crewId}-${row.aircraftId}-${idx}`;
                      const isExpanded = !!expandedRows[rowKey];
                      const trips = row.trips || [];

                      return (
                        <React.Fragment key={rowKey}>
                          <TableRow
                            hover
                            sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}
                            onClick={() => toggleRowExpansion(rowKey)}
                          >
                            <TableCell padding="checkbox">
                              <IconButton
                                size="small"
                                aria-label={isExpanded ? 'Hide trip details' : 'Show trip details'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowExpansion(rowKey);
                                }}
                              >
                                <ExpandMoreIcon
                                  sx={{
                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s'
                                  }}
                                />
                              </IconButton>
                            </TableCell>
                            <TableCell>{row.crewName || '—'}</TableCell>
                            <TableCell>{row.aircraftRegistration || '—'}</TableCell>
                            <TableCell>{row.aircraftType || '—'}</TableCell>
                            <TableCell align="right">{(row.totalHours || 0).toFixed(1)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={5} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 1.5, px: 1 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Trip / charter details
                                  </Typography>
                                  {trips.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      No trip details available for this pilot and aircraft.
                                    </Typography>
                                  ) : (
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Charter / Trip</TableCell>
                                          <TableCell>Type</TableCell>
                                          <TableCell>Route</TableCell>
                                          <TableCell>Duty start</TableCell>
                                          <TableCell>Duty end</TableCell>
                                          <TableCell>Takeoff / Landing</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell align="right">Hours</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {trips.map((trip) => (
                                          <TableRow key={trip.entryId}>
                                            <TableCell>
                                              {trip.charterNumber || trip.tripTitle || '—'}
                                              {trip.charterNumber && trip.tripTitle && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                  {trip.tripTitle}
                                                </Typography>
                                              )}
                                              {trip.sectorSeqNo != null && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                  Sector {trip.sectorSeqNo}
                                                </Typography>
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {formatDutyLabel(trip)}
                                              {trip.flightType && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                  {trip.flightType}
                                                </Typography>
                                              )}
                                            </TableCell>
                                            <TableCell>{trip.route || '—'}</TableCell>
                                            <TableCell>{formatDateTime(trip.dutyStartTime)}</TableCell>
                                            <TableCell>{formatDateTime(trip.dutyEndTime)}</TableCell>
                                            <TableCell>
                                              {trip.takeOffTime || trip.landingTime ? (
                                                <>
                                                  <Typography variant="body2">
                                                    {trip.takeOffTime ? formatDateTime(trip.takeOffTime) : '—'}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary">
                                                    to {trip.landingTime ? formatDateTime(trip.landingTime) : '—'}
                                                  </Typography>
                                                </>
                                              ) : (
                                                '—'
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                label={trip.status || '—'}
                                                size="small"
                                                color={getStatusColor(trip.status)}
                                              />
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatDuration(trip.hours)}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CrewDutyRosterDashboard;
